import { useEffect, useMemo, useState } from "react";
import { api, formatCents, API_BASE, getToken } from "@/lib/api";
import { PageHeader, Card, Pill, Loader, Button } from "@/components/ui/Primitives";
import {
  TrendingUp, TrendingDown, Wallet, Receipt, Landmark, FileDown,
  CheckCircle2, AlertCircle, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const RANGES = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "Year to date" },
  { key: "last_year", label: "Last year" },
];

const TABS = [
  { key: "pnl", label: "P&L", icon: TrendingUp },
  { key: "expenses", label: "Expense ledger", icon: Receipt },
  { key: "reconciliation", label: "Bank reconciliation", icon: Landmark },
  { key: "export", label: "Export", icon: FileDown },
];

function fmtCents(c) {
  return formatCents(c || 0);
}

export default function Books() {
  const [tab, setTab] = useState("pnl");
  const [range, setRange] = useState("this_month");

  return (
    <div data-testid="books-page">
      <PageHeader
        title="Books"
        subtitle="P&L per property, expense ledger, bank reconciliation and CRA-ready exports."
        actions={
          <select
            data-testid="books-range-select"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="px-3 py-2 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700"
          >
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 -mt-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`books-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                active
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "pnl" && <PnLTab range={range} />}
      {tab === "expenses" && <ExpensesTab range={range} />}
      {tab === "reconciliation" && <ReconciliationTab />}
      {tab === "export" && <ExportTab range={range} />}
    </div>
  );
}

function PnLTab({ range }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/books/pnl?range=${range}`)
      .then((r) => { if (mounted) setD(r.data); })
      .catch(() => toast.error("Couldn't load P&L"))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [range]);

  if (loading) return <Loader />;
  if (!d) return null;
  const noi = d.noi_cents || 0;
  const positive = noi >= 0;

  return (
    <div className="space-y-5" data-testid="books-pnl-content">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiTile testid="pnl-revenue" label="Revenue" value={fmtCents(d.revenue_cents)} sub="Rent collected" icon={Wallet} tone="emerald" />
        <KpiTile testid="pnl-expenses" label="Expenses" value={fmtCents(d.expenses_cents)} sub="Operating costs" icon={Receipt} tone="rose" />
        <KpiTile testid="pnl-noi" label={positive ? "Net operating income" : "Net loss"} value={fmtCents(Math.abs(noi))} sub={positive ? "After expenses" : "Below break-even"} icon={positive ? TrendingUp : TrendingDown} tone={positive ? "sky" : "rose"} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6" data-testid="pnl-by-property">
          <h3 className="font-display text-base font-bold text-slate-900 mb-4">By property</h3>
          {(d.by_property || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No activity in this range yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {d.by_property.map((p) => (
                <div key={p.property_id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Rev {fmtCents(p.revenue_cents)} · Exp {fmtCents(p.expenses_cents)}</div>
                  </div>
                  <div className={`text-sm font-bold tabular-nums ${p.noi_cents >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                    {p.noi_cents >= 0 ? "+" : "−"}{fmtCents(Math.abs(p.noi_cents))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="pnl-by-category">
          <h3 className="font-display text-base font-bold text-slate-900 mb-4">Expenses by category</h3>
          {(d.expenses_by_category || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No expenses logged yet.</div>
          ) : (
            <div className="space-y-2.5">
              {d.expenses_by_category.map((c) => {
                const pct = d.expenses_cents > 0 ? Math.round((c.amount_cents / d.expenses_cents) * 100) : 0;
                return (
                  <div key={c.key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-slate-700">{c.label}</span>
                      <span className="text-slate-500 tabular-nums">{fmtCents(c.amount_cents)} · {pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ExpensesTab({ range }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/books/expenses?range=${range}&limit=300`)
      .then((r) => { if (mounted) setItems(r.data?.items || []); })
      .catch(() => toast.error("Couldn't load expense ledger"))
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [range]);

  if (loading) return <Loader />;

  return (
    <Card className="overflow-hidden" data-testid="books-expenses-content">
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500">
          No expenses logged for this range yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Date</th>
                <th className="text-left px-5 py-3 font-semibold">Category</th>
                <th className="text-left px-5 py-3 font-semibold">Description</th>
                <th className="text-left px-5 py-3 font-semibold">Property</th>
                <th className="text-right px-5 py-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((r) => (
                <tr key={r.id || `${r.incurred_at}-${r.description}`} className="hover:bg-slate-50">
                  <td className="px-5 py-3 text-slate-700 tabular-nums">{r.incurred_at}</td>
                  <td className="px-5 py-3"><Pill tone="slate">{r.category_label}</Pill></td>
                  <td className="px-5 py-3 text-slate-900">{r.description || "—"}</td>
                  <td className="px-5 py-3 text-slate-500">{r.property_name || "—"}</td>
                  <td className="px-5 py-3 text-right font-semibold text-rose-600 tabular-nums">−{fmtCents(r.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ReconciliationTab() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get(`/books/reconciliation?days=60`)
      .then((r) => setD(r.data))
      .catch(() => toast.error("Couldn't load reconciliation"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  const rerunMatcher = async () => {
    setRunning(true);
    try {
      await api.post("/cash-app/run", {});
      toast.success("Matcher re-ran");
      reload();
    } catch {
      toast.error("Couldn't re-run matcher");
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <Loader />;
  if (!d) return null;

  if (!d.has_bank_link) {
    return (
      <Card className="p-10 text-center" data-testid="books-recon-no-bank">
        <Landmark className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <div className="font-display text-lg font-bold text-slate-900">Bank reconciliation coming soon</div>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Automatic deposit matching will be available once our bank-link partner finishes onboarding. We&apos;ll enable it for your account as soon as it&apos;s ready.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="books-recon-content">
      <div className="grid grid-cols-2 gap-4">
        <KpiTile testid="recon-matched" label="Matched" value={d.matched_count} sub="Auto-applied to leases" icon={CheckCircle2} tone="emerald" />
        <KpiTile testid="recon-unreconciled" label="Unreconciled" value={d.unreconciled_count} sub="Need classification" icon={AlertCircle} tone="amber" />
      </div>

      <div className="flex justify-end">
        <button onClick={rerunMatcher} disabled={running} data-testid="recon-rerun-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} /> Re-run matcher
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-6" data-testid="recon-unreconciled-list">
          <h3 className="font-display text-base font-bold text-slate-900 mb-3">Unreconciled bank transactions</h3>
          {d.unreconciled.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">Everything&apos;s matched. </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {d.unreconciled.map((tx, i) => (
                <div key={tx.tx_id || i} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{tx.description || "Bank transaction"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{tx.date}</div>
                  </div>
                  <div className={`text-sm font-bold tabular-nums ${tx.amount >= 0 ? "text-emerald-700" : "text-slate-700"}`}>
                    {tx.amount >= 0 ? "+" : ""}{Number(tx.amount).toLocaleString("en-CA", { style: "currency", currency: "CAD" })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="recon-matched-list">
          <h3 className="font-display text-base font-bold text-slate-900 mb-3">Matched to leases</h3>
          {d.matched.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No matches yet — the matcher runs nightly.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {d.matched.map((tx, i) => (
                <div key={tx.tx_id || i} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-none" />
                      {tx.description || "Bank transaction"}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 ml-5">{tx.date}</div>
                  </div>
                  <div className="text-sm font-bold tabular-nums text-emerald-700">
                    {Number(tx.amount).toLocaleString("en-CA", { style: "currency", currency: "CAD" })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ExportTab({ range }) {
  const triggerDownload = (kind) => {
    const token = getToken();
    const url = `${API_BASE}/books/export.csv?kind=${kind}&range=${range}`;
    // Use fetch with auth header so we can stream the CSV
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const dl = URL.createObjectURL(blob);
        a.href = dl;
        a.download = `${kind === "t776" ? "t776" : "general_ledger"}_${range}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(dl);
        toast.success("Download ready");
      })
      .catch(() => toast.error("Couldn't generate the CSV"));
  };

  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="books-export-content">
      <Card className="p-7" data-testid="export-t776">
        <FileDown className="w-9 h-9 text-emerald-600 mb-4" />
        <div className="font-display text-lg font-bold text-slate-900">T776 CRA worksheet</div>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          One row per CRA T776 line — gross rents on top, every expense bucket mapped to its T776 label, ending in net rental income. Hand this directly to your accountant.
        </p>
        <button onClick={() => triggerDownload("t776")} data-testid="export-t776-btn"
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
          Download T776 CSV
        </button>
      </Card>

      <Card className="p-7" data-testid="export-ledger">
        <Receipt className="w-9 h-9 text-sky-600 mb-4" />
        <div className="font-display text-lg font-bold text-slate-900">General ledger</div>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          One row per income and expense transaction, with property + category. Use this for full audit trail or to import into QuickBooks / Xero.
        </p>
        <button onClick={() => triggerDownload("ledger")} data-testid="export-ledger-btn"
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50">
          Download ledger CSV
        </button>
      </Card>
    </div>
  );
}

function KpiTile({ testid, label, value, sub, icon: Icon, tone = "emerald" }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700",
    sky: "bg-sky-50 text-sky-700",
    rose: "bg-rose-50 text-rose-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">{label}</div>
          <div className="mt-2 font-display text-2xl font-bold text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone] || tones.emerald}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}
